import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

/**
 * User Management Service
 * Handles all user-related API calls
 */

const userCache = new Map();
const pendingUserRequests = new Map();

export const userService = {
  /**
   * Get user by username
   * @param {string} username - Username
   * @returns {Promise<Object>} User data
   */
  getUserByUsername: async (username) => {
    if (!username) {
      return null;
    }

    if (userCache.has(username)) {
      return userCache.get(username);
    }

    if (pendingUserRequests.has(username)) {
      return pendingUserRequests.get(username);
    }

    const promise = axios
      .get(`${USER_URL}/api/users/username/${username}`, {
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
      })
      .then((response) => {
        const data = response.data;
        userCache.set(username, data);
        pendingUserRequests.delete(username);
        return data;
      })
      .catch((err) => {
        pendingUserRequests.delete(username);
        throw err;
      });

    pendingUserRequests.set(username, promise);
    return promise;
  },

  /**
   * Get users by account ID
   * @param {string} accountId - Account ID
   * @param {Object} options - Pagination and filter options
   * @param {number} options.page - Zero-based page index
   * @param {number} options.size - Page size
   * @param {string} [options.search] - Search term (optional)
   * @param {string} [options.status] - Status filter e.g. 'ACTIVE' | 'INACTIVE' (optional)
   * @param {string} [options.role] - Role filter e.g. 'ADMIN' | 'USER' (optional)
   * @param {string} [options.sortBy] - Sort field (optional)
   * @param {string} [options.sortDirection] - Sort direction 'ASC' | 'DESC' (optional)
   * @returns {Promise<Object>} Paginated response { content, page, size, totalPages, totalElements, numberOfElements }
   */
  getUsersByAccount: async (
    accountId,
    { page = 0, size = 10, search, status, role, sortBy, sortDirection } = {}
  ) => {
    try {
      const params = { page, size };
      if (search) params.search = search;
      if (status) params.status = status;
      if (role) params.role = role;
      if (sortBy) params.sortBy = sortBy;
      if (sortDirection) params.sortDirection = sortDirection;

      const response = await axios.get(`${USER_URL}/api/users/account/${accountId}`, {
        params,
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
      });
      // API returns { success, message, data: { ...pagination, content: [...] } }
      const data = response?.data?.data;
      return (
        data || {
          content: [],
          page,
          size,
          totalPages: 0,
          totalElements: 0,
          numberOfElements: 0,
        }
      );
    } catch (err) {
      // Normalize 404 to an empty page response
      if (err.response && err.response.status === 404) {
        return { content: [], page, size, totalPages: 0, totalElements: 0, numberOfElements: 0 };
      }
      throw err;
    }
  },

  /**
   * Get all users (Super Admin) with pagination and optional filters
   * @param {Object} options - Pagination and filter options
   * @param {number} options.page - Zero-based page index
   * @param {number} options.size - Page size
   * @param {string} [options.search] - Search term (optional)
   * @param {string} [options.status] - Status filter e.g. 'ACTIVE' | 'INACTIVE' (optional)
   * @param {string} [options.role] - Role filter e.g. 'ADMIN' | 'USER' (optional)
   * @param {string} [options.sortBy] - Sort field (optional)
   * @param {string} [options.sortDirection] - Sort direction 'ASC' | 'DESC' (optional)
   * @returns {Promise<Object>} Paginated response { content, page, size, totalPages, totalElements, numberOfElements }
   */
  getAllUsers: async ({ page = 0, size = 10, search, status, role, sortBy, sortDirection } = {}) => {
    const params = { page, size };
    if (search) params.search = search;
    if (status) params.status = status;
    if (role) params.role = role;
    if (sortBy) params.sortBy = sortBy;
    if (sortDirection) params.sortDirection = sortDirection;

    const response = await axios.get(`${USER_URL}/api/users/all`, {
      params,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data?.data || { content: [], page, size, totalPages: 0, totalElements: 0, numberOfElements: 0 };
  },

  /**
   * Get all users for super admin
   * @returns {Promise<Array>} All users list
   */
  getAllUsersForSuperAdmin: async () => {
    const response = await axios.get(`${USER_URL}/api/users/super-admin/all-users`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Create new user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} Created user data
   */
  createUser: async (userData) => {
    const response = await axios.post(`${USER_URL}/api/users/create`, userData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user data
   */
  updateUser: async (userId, userData) => {
    const response = await axios.put(`${USER_URL}/api/users/update/${userId}`, userData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Update registered user
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user data
   */
  updateRegisteredUser: async (userId, userData) => {
    const response = await axios.post(`${USER_URL}/api/users/updateRegisterUser/${userId}`, userData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Delete user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteUser: async (userId) => {
    const response = await axios.delete(`${USER_URL}/api/users/delete/${userId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Change user password
   * @param {Object} passwordData - Password change data
   * @returns {Promise<Object>} Password change response
   */
  changePassword: async (passwordData) => {
    const response = await axios.post(`${USER_URL}/api/users/change-password`, passwordData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get user locales
   * @returns {Promise<Array>} Available locales
   */
  getLocales: async () => {
    const response = await axios.get(`${USER_URL}/api/users/locales`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  }
};

export default userService;
